/**
 * CloudUI - File menu, Publish button, account menu + cloud state
 * (CLOUD_SYNC_SPEC.md §8). Owns the cloud services and gates every cloud
 * action behind sign-in; the local Import / Download-a-copy fallbacks stay
 * available offline.
 */
import { AuthService } from '../../cloud/AuthService.js';
import { initAnalytics, track } from '../../cloud/Analytics.js';
import { CloudClient, AuthRequiredError } from '../../cloud/CloudClient.js';
import { DesignService } from '../../cloud/DesignService.js';
import { PublishService } from '../../cloud/PublishService.js';
import { ICONS, phIcon } from '../icons.js';
import {
    injectCloudStyles, showSignInDialog, showSaveAsDialog,
    showOpenDialog, showPublishDialog, showAdminReviewDialog
} from './dialogs.js';

const isMac = navigator.platform?.toUpperCase().includes('MAC') ||
    navigator.userAgent.includes('Mac');
const MOD = isMac ? '⌘' : 'Ctrl+';

export class CloudUI {
    constructor(app) {
        this.app = app;
        this.auth = new AuthService();
        this.client = new CloudClient(this.auth);
        this.designs = new DesignService(this.client, app);
        this.publishSvc = new PublishService(this.client);
        this.apiUser = null;     // users row from /auth/me (handle etc.)
        this._openMenu = null;

        injectCloudStyles();
        this._buildTopLeft();
        this._buildTopRight();

        this.auth.onAuthChange((user) => this._onAuthChange(user));
        this.designs.onStateChange(() => this._updateStatus());

        // Restore session in the background (loads the firebase chunk)
        this.auth.init().catch(e => console.warn('auth init failed:', e));

        // GA4 page/session tracking + cloud-action events (prod only,
        // fail-silent - see Analytics.js)
        initAnalytics();

        // Any click outside an open menu closes it
        document.addEventListener('pointerdown', (e) => {
            if (this._openMenu && !this._openMenu.contains(e.target) &&
                !e.target.closest?.('[data-sc-menu-btn]')) {
                this._closeMenu();
            }
        }, true);
    }

    // --- Layout ------------------------------------------------------------

    _buildTopLeft() {
        // [≡ File ▾] button flows INSIDE the #logo row (right of the logo +
        // beta pill) so it stays aligned with the wordmark at any size
        this.fileBtn = document.createElement('button');
        this.fileBtn.id = 'file-menu-btn';
        this.fileBtn.dataset.scMenuBtn = '1';
        this.fileBtn.style.cssText = `
            display: flex; align-items: center; gap: 6px;
            margin-left: 6px;
            padding: 7px 10px; border: 1px solid rgba(0,0,0,0.08);
            border-radius: 12px;
            background: var(--surface);
            backdrop-filter: blur(18px) saturate(1.4);
            -webkit-backdrop-filter: blur(18px) saturate(1.4);
            box-shadow: var(--shadow-soft);
            font-size: 13px; font-weight: 600; color: var(--text);
            cursor: pointer; pointer-events: auto;
        `;
        this.fileBtn.append(
            phIcon(ICONS.list, 16),
            document.createTextNode('File'),
            phIcon(ICONS.caretDown, 11)
        );
        this.fileBtn.addEventListener('click', () => this._toggleFileMenu());

        // Cloud status label under the logo row: "My Chair — v3 • edited"
        this.statusEl = document.createElement('div');
        this.statusEl.id = 'cloud-status';
        this.statusEl.style.cssText = `
            position: absolute; top: 56px; left: 18px; z-index: 2050;
            font-size: 11px; color: var(--text-dim);
            background: var(--surface); border-radius: 8px;
            padding: 2px 8px; box-shadow: var(--shadow-soft);
            display: none; pointer-events: none; white-space: nowrap;
        `;

        const logo = document.getElementById('logo');
        if (logo) {
            logo.appendChild(this.fileBtn);
        } else {
            // No logo in the DOM (tests/embeds): float it top-left
            this.fileBtn.style.position = 'absolute';
            this.fileBtn.style.top = '16px';
            this.fileBtn.style.left = '16px';
            this.app.uiManager.uiContainer.appendChild(this.fileBtn);
        }
        this.app.uiManager.uiContainer.appendChild(this.statusEl);
    }

    _buildTopRight() {
        this.topBar = document.createElement('div');
        this.topBar.id = 'top-bar-right';
        this.topBar.style.cssText = `
            position: absolute; top: 16px; right: 16px; z-index: 2100;
            display: flex; align-items: center; gap: 8px;
            pointer-events: auto;
        `;

        // Publish (Figma-style primary button)
        this.publishBtn = document.createElement('button');
        this.publishBtn.className = 'sc-btn sc-btn-primary';
        this.publishBtn.style.boxShadow = 'var(--shadow-soft)';
        this.publishBtn.append(document.createTextNode('Publish'));
        this.publishBtn.addEventListener('click', () => this.openPublishDialog());

        // Account: Sign-in button OR avatar menu
        this.accountBtn = document.createElement('button');
        this.accountBtn.dataset.scMenuBtn = '1';
        this.accountBtn.style.cssText = `
            display: flex; align-items: center; justify-content: center;
            gap: 6px; height: 36px; min-width: 36px; padding: 0 10px;
            border: 1px solid rgba(0,0,0,0.08); border-radius: 12px;
            background: var(--surface);
            backdrop-filter: blur(18px) saturate(1.4);
            -webkit-backdrop-filter: blur(18px) saturate(1.4);
            box-shadow: var(--shadow-soft);
            font-size: 13px; font-weight: 600; color: var(--text);
            cursor: pointer;
        `;
        this.accountBtn.addEventListener('click', () => {
            if (this.auth.isSignedIn) this._toggleAccountMenu();
            else this.signIn();
        });

        this.topBar.append(this.publishBtn, this.accountBtn);
        this.app.uiManager.uiContainer.appendChild(this.topBar);
        this._renderAccountButton();
    }

    _renderAccountButton() {
        const user = this.auth.currentUser;
        this.accountBtn.innerHTML = '';
        if (!user) {
            // Logged out: labelled pill
            this.accountBtn.style.width = 'auto';
            this.accountBtn.style.padding = '0 10px';
            this.accountBtn.style.borderRadius = '12px';
            this.accountBtn.title = '';
            this.accountBtn.append(
                phIcon(ICONS.userCircle, 17),
                document.createTextNode('Sign in')
            );
            return;
        }
        // Signed in: just the avatar, a clean 36px circle
        this.accountBtn.style.width = '36px';
        this.accountBtn.style.padding = '0';
        this.accountBtn.style.borderRadius = '50%';
        this.accountBtn.style.overflow = 'hidden';
        this.accountBtn.title = user.displayName || user.email || '';
        if (user.photoURL) {
            const img = document.createElement('img');
            img.src = user.photoURL;
            img.referrerPolicy = 'no-referrer';
            img.style.cssText =
                'width: 100%; height: 100%; object-fit: cover; display: block;';
            this.accountBtn.appendChild(img);
        } else {
            const initial = document.createElement('span');
            initial.style.cssText = `
                width: 100%; height: 100%;
                background: var(--accent); color: #fff;
                display: flex; align-items: center; justify-content: center;
                font-size: 14px; font-weight: 700;`;
            initial.textContent =
                (user.displayName || user.email || '?')[0].toUpperCase();
            this.accountBtn.appendChild(initial);
        }
    }

    // --- Menus ---------------------------------------------------------------

    _closeMenu() {
        this._openMenu?.remove();
        this._openMenu = null;
    }

    _menu(anchor, { alignRight = false } = {}) {
        this._closeMenu();
        const menu = document.createElement('div');
        menu.className = 'sc-menu';
        const r = anchor.getBoundingClientRect();
        menu.style.top = `${r.bottom + 6}px`;
        if (alignRight) menu.style.right = `${window.innerWidth - r.right}px`;
        else menu.style.left = `${r.left}px`;
        this.app.uiManager.uiContainer.appendChild(menu);
        this._openMenu = menu;
        return menu;
    }

    _menuItem(menu, { icon, label, kbd, disabled, onClick }) {
        const item = document.createElement('button');
        item.className = 'sc-menu-item';
        if (icon) item.appendChild(phIcon(icon, 16));
        const text = document.createElement('span');
        text.textContent = label;
        item.appendChild(text);
        if (kbd) {
            const k = document.createElement('span');
            k.className = 'sc-kbd';
            k.textContent = kbd;
            item.appendChild(k);
        }
        if (disabled) item.disabled = true;
        item.addEventListener('click', () => {
            this._closeMenu();
            onClick?.();
        });
        menu.appendChild(item);
        return item;
    }

    _menuSep(menu) {
        const sep = document.createElement('div');
        sep.className = 'sc-menu-sep';
        menu.appendChild(sep);
    }

    _toggleFileMenu() {
        if (this._openMenu) return this._closeMenu();
        const menu = this._menu(this.fileBtn);
        const signedIn = this.auth.isSignedIn;

        if (!signedIn) {
            this._menuItem(menu, {
                icon: ICONS.userCircle, label: 'Sign in / Register',
                onClick: () => this.signIn()
            });
            this._menuSep(menu);
        }
        this._menuItem(menu, {
            icon: ICONS.filePlus, label: 'New',
            onClick: () => this.newDesign()
        });
        this._menuItem(menu, {
            icon: ICONS.floppyDisk, label: 'Save', kbd: `${MOD}S`,
            onClick: () => this.save()
        });
        this._menuItem(menu, {
            icon: ICONS.floppyDisk, label: 'Save As…',
            onClick: () => this.saveAs()
        });
        this._menuItem(menu, {
            icon: ICONS.folderOpen, label: 'Open…', kbd: `${MOD}O`,
            onClick: () => this.openDialog()
        });
        this._menuSep(menu);
        this._menuItem(menu, {
            icon: ICONS.uploadSimple, label: 'Import file…',
            onClick: () => this.app.loadDesignFromFile()
        });
        this._menuItem(menu, {
            icon: ICONS.downloadSimple, label: 'Download a copy…',
            kbd: `${MOD}⇧S`,
            onClick: () => this.app.saveDesignToFile()
        });
    }

    _toggleAccountMenu() {
        if (this._openMenu) return this._closeMenu();
        const menu = this._menu(this.accountBtn, { alignRight: true });
        const user = this.auth.currentUser;

        const label = document.createElement('div');
        label.className = 'sc-menu-label';
        label.textContent = user?.displayName || user?.email || '';
        menu.appendChild(label);
        this._menuSep(menu);

        this._menuItem(menu, {
            icon: ICONS.folderOpen, label: 'My designs',
            onClick: () => this.openDialog()
        });
        this._menuItem(menu, {
            icon: ICONS.books, label: 'Library',
            onClick: () => window.open(`${this.client.base}/library`, '_blank')
        });
        if (this.apiUser?.is_admin) {
            this._menuItem(menu, {
                icon: ICONS.check, label: 'Review submissions',
                onClick: () => showAdminReviewDialog({
                    client: this.client, app: this.app
                })
            });
        }
        this._menuSep(menu);
        this._menuItem(menu, {
            icon: ICONS.signOut, label: 'Sign out',
            onClick: async () => {
                await this.auth.signOut();
                this.designs.resetToLocal();
                this.app.showToast('Signed out');
            }
        });
    }

    // --- Auth ------------------------------------------------------------------

    async _onAuthChange(user) {
        this._renderAccountButton();
        this._updateStatus();
        if (user) {
            // Materialize/refresh the users row; grab handle for publish
            try {
                const { user: apiUser } = await this.client.get('/auth/me');
                this.apiUser = apiUser;
            } catch (e) {
                console.warn('auth/me failed:', e.message);
            }
            // Reattach the cloud design identity after a reload
            this.designs.restoreIdentity();
        } else {
            this.apiUser = null;
        }
    }

    /** Open the sign-in dialog; resolves the user or null */
    signIn(reason = null) {
        return showSignInDialog(this.auth, { reason });
    }

    /** Ensure signed in (dialog if needed). Returns user or null. */
    async _requireSignIn(reason) {
        await this.auth.init();
        if (this.auth.isSignedIn) return this.auth.currentUser;
        return this.signIn(reason);
    }

    // --- Actions (File menu / keybindings) --------------------------------------

    /** File > New: clear everything, detach from the cloud design */
    newDesign() {
        // Select all planes so Clear really empties the whole document
        this.app.basePlanes.forEach((p, i) => {
            this.app.selectedBasePlaneIndices.add(i);
            p.select();
        });
        this.app.clearAll();
        this.designs.resetToLocal();
        this._updateStatus();
    }

    /**
     * ⌘S / File > Save: new cloud version of the current design; first
     * save prompts for a name (SaveAs dialog).
     */
    async save() {
        const user = await this._requireSignIn('Sign in to save designs to the cloud');
        if (!user) return;
        try {
            if (!this.designs.currentDesignId) {
                const opts = await showSaveAsDialog({ title: 'Save to cloud' });
                if (!opts) return;
                await this.designs.save(opts);
            } else {
                await this.designs.save();
            }
            this.app.showToast(`Saved v${this.designs.currentVersion} to the cloud`);
        } catch (e) {
            if (e instanceof AuthRequiredError) return this.save();
            this.app.showToast(`Save failed: ${e.message}`);
            console.error('cloud save failed:', e);
        }
    }

    /** File > Save As: always a new cloud design */
    async saveAs() {
        const user = await this._requireSignIn('Sign in to save designs to the cloud');
        if (!user) return;
        const opts = await showSaveAsDialog({
            title: 'Save As',
            name: this.designs.currentName ? `${this.designs.currentName} copy` : '',
            comment: this.designs.currentComment || ''
        });
        if (!opts) return;
        try {
            if (this._forkSource) {
                // Saving a viewed allow-edit share: a fork (D4)
                opts.forkedFrom = this._forkSource;
                this._forkSource = null;
            }
            await this.designs.saveAs(opts);
            this.app.showToast(`Saved "${opts.name || 'Untitled'}" to the cloud`);
        } catch (e) {
            this.app.showToast(`Save failed: ${e.message}`);
            console.error('cloud saveAs failed:', e);
        }
    }

    /** ⌘O / File > Open: pick from your designs */
    async openDialog() {
        const user = await this._requireSignIn('Sign in to open your cloud designs');
        if (!user) return;
        const id = await showOpenDialog(this.designs);
        if (!id) return;
        try {
            const meta = await this.designs.open(id);
            this.app.showToast(`Opened "${meta.name || 'Untitled'}" (v${meta.latest_version})`);
        } catch (e) {
            this.app.showToast(`Open failed: ${e.message}`);
            console.error('cloud open failed:', e);
        }
    }

    /** Publish button */
    async openPublishDialog() {
        const user = await this._requireSignIn('Sign in to publish designs');
        if (!user) return;
        // A just-clicked ⌘S may still be in flight - wait so the dialog's
        // "currently vN / will publish vM" banner reads settled state
        try { await this.designs.pendingSave; } catch { /* surfaced by save() */ }
        if (!this.designs.currentDesignId || this.designs.currentVersion < 1) {
            // Must exist in the cloud first (spec: default promote = latest save)
            this.app.showToast('Save the design first (⌘S), then publish');
            await this.save();
            if (!this.designs.currentDesignId) return;
        }
        await showPublishDialog({
            publishSvc: this.publishSvc,
            designs: this.designs,
            user: this.apiUser,
            app: this.app
        });
        // Refresh handle (may have been set during first publish)
        this._onAuthChange(this.auth.currentUser);
    }

    // --- Share deep-links (?d=<slug>[&k=<token>]) ---------------------------

    /** Is the app being opened via a share link? (checked before autosave restore) */
    hasShareLink() {
        return new URLSearchParams(location.search).has('d');
    }

    /** Load the shared design the URL points at (replaces autosave restore) */
    async openShareLink() {
        const params = new URLSearchParams(location.search);
        const slug = params.get('d');
        const token = params.get('k');
        history.replaceState(null, '', location.pathname); // one-shot
        try {
            const shared = await this.publishSvc.openShared(slug, token);
            if (!this.app.loadDesign(shared.design)) {
                throw new Error('could not load the design (see console)');
            }
            this.designs.resetToLocal();
            this.designs.currentName = shared.title;
            // Save As of an allow-edit share records fork provenance (D4)
            this._forkSource = shared.allow_edit
                ? shared.published_version_id : null;
            this.app.showToast(`Viewing "${shared.title}" by ${shared.author}` +
                (shared.allow_edit ? ' — Save As to make it yours' : ''));
            track('share_link_open', { slug, allow_edit: !!shared.allow_edit });
        } catch (e) {
            console.error('share link failed:', e);
            this.app.showToast(`Could not open shared design: ${e.message}`);
            this.app.restoreAutosave();
        }
    }

    /**
     * Autosave hook (called from DesignIO on every debounced change):
     * mark dirty + push the cloud working draft (D2 - never a version).
     */
    onLocalChange() {
        this.designs.markDirty();
        this.designs.scheduleDraftSave();
    }

    // --- Status line -------------------------------------------------------------

    _updateStatus() {
        const d = this.designs;
        if (!this.auth.isSignedIn || !d.currentDesignId) {
            this.statusEl.style.display = 'none';
            document.title = 'SketchChair - Parametric Furniture Design';
            return;
        }
        const name = d.currentName || 'Untitled';
        const parts = [`${name} — v${d.currentVersion}`];
        if (d.dirty) parts.push('edited');
        this.statusEl.textContent = parts.join(' • ');
        this.statusEl.style.display = 'block';
        document.title = `${name}${d.dirty ? ' •' : ''} - SketchChair`;
    }
}
